import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaPlus, FaTrash, FaEdit, FaEye } from 'react-icons/fa';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import { apiFetch, getAuthHeaders } from '../utils/apiUtils';
import '../styles/buttons.css';

// Custom tooltip styles similar to SpiceRack.js with mobile overflow prevention
const tooltipStyles = `
  .admin-tooltip {
    position: relative;
  }
  
  .admin-tooltip::before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 6px 10px;
    border-radius: 6px;
    font-family: 'VT323', monospace;
    font-size: 12px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s, visibility 0.2s;
    transition-delay: 0.5s;
    z-index: 1000;
    pointer-events: none;
    margin-bottom: 5px;
    max-width: 200px;
    
    /* Mobile-specific styles */
    @media (max-width: 640px) {
      white-space: normal;
      word-wrap: break-word;
      max-width: 150px;
      text-align: center;
      line-height: 1.2;
    }
    
    /* Prevent extending beyond viewport */
    @media (max-width: 768px) {
      left: 0;
      right: 0;
      transform: none;
      margin-left: auto;
      margin-right: auto;
      width: fit-content;
      max-width: calc(100vw - 40px);
    }
  }
  
  .admin-tooltip::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s, visibility 0.2s;
    transition-delay: 0.5s;
    z-index: 1000;
    pointer-events: none;
    margin-bottom: 1px;
    
    /* Mobile-specific arrow positioning */
    @media (max-width: 768px) {
      left: 50%;
      transform: translateX(-50%);
    }
  }
  
  .admin-tooltip:hover::before,
  .admin-tooltip:hover::after {
    opacity: 1;
    visibility: visible;
  }
`;

// Inject styles into the document
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = tooltipStyles;
  document.head.appendChild(styleElement);
}

const SpiceRackAdmin = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [deletingItem, setDeletingItem] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/api/stylekit');
      setConfig(data);
      setOriginalConfig(JSON.parse(JSON.stringify(data))); // Deep clone for comparison
      // Initially expand all categories
      const expanded = {};
      Object.keys(data).forEach(key => {
        if (key !== 'basePromptStructure') {
          expanded[key] = true;
        }
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load SpiceRack configuration');
    } finally {
      setLoading(false);
    }
  };

  // Check if configuration has been modified
  const hasChanges = () => {
    return JSON.stringify(config) !== JSON.stringify(originalConfig);
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const authHeaders = await getAuthHeaders(getAccessTokenSilently);
      await apiFetch('/api/stylekit', {
        method: 'PUT',
        body: JSON.stringify(config),
        headers: authHeaders,
      });
      toast.success('SpiceRack configuration saved successfully!');
      setOriginalConfig(JSON.parse(JSON.stringify(config))); // Update original after save
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const addItem = (categoryKey) => {
    const newItem = {
      id: `${categoryKey}-${Date.now()}`,
      emoji: '🎨',
      name: 'New Item',
      prompt: '',
      description: ''
    };
    
    setConfig(prev => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        items: [...prev[categoryKey].items, newItem]
      }
    }));
    
    setEditingItem(newItem.id);
  };

  const deleteItem = (categoryKey, itemId) => {
    setConfig(prev => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        items: prev[categoryKey].items.filter(item => item.id !== itemId)
      }
    }));
    setEditingItem(null);
    setDeletingItem(null);
  };

  const handleDeleteClick = (categoryKey, itemId) => {
    setDeletingItem({ categoryKey, itemId });
  };

  const confirmDelete = () => {
    if (deletingItem) {
      deleteItem(deletingItem.categoryKey, deletingItem.itemId);
    }
  };

  const cancelDelete = () => {
    setDeletingItem(null);
  };

  const updateItem = (categoryKey, itemId, field, value) => {
    setConfig(prev => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        items: prev[categoryKey].items.map(item =>
          item.id === itemId ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const updateCategory = (categoryKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        [field]: value
      }
    }));
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-[#00FF9F]/30 border-t-[#00FF9F] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12">
        <p className="text-black/60 font-['VT323'] text-lg">Failed to load configuration</p>
      </div>
    );
  }

  return (
    <div className="max-w-[920px] mx-auto">
      {hasChanges() && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <button
            onClick={saveConfig}
            disabled={saving}
            className="btn btn-green"
          >
            <FaSave />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </motion.div>
      )}

      {/* Base Prompt Structure */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 bg-white rounded-xl p-6 shadow-sm"
      >
        <h3 className="text-lg font-['VT323'] text-black/80 mb-4">Base Prompt Structure</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-['VT323'] text-black/60 mb-2">Prefix</label>
            <input
              type="text"
              value={config.basePromptStructure.prefix}
              onChange={(e) => setConfig(prev => ({
                ...prev,
                basePromptStructure: { ...prev.basePromptStructure, prefix: e.target.value }
              }))}
              className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-['VT323'] text-black/60 mb-2">Suffix</label>
            <input
              type="text"
              value={config.basePromptStructure.suffix}
              onChange={(e) => setConfig(prev => ({
                ...prev,
                basePromptStructure: { ...prev.basePromptStructure, suffix: e.target.value }
              }))}
              className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
            />
          </div>
        </div>
      </motion.div>

      {/* Categories */}
      {Object.entries(config).map(([categoryKey, category], index) => {
        if (categoryKey === 'basePromptStructure') return null;
        
        return (
          <div
            key={categoryKey}
            className="mb-6 bg-white rounded-xl shadow-sm overflow-hidden"
          >
            <div
              className="flex items-center justify-between p-4 bg-gradient-to-r from-[#00FF9F]/10 to-[#00FF9F]/5 cursor-pointer"
              onClick={() => toggleCategory(categoryKey)}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{category.emoji}</span>
                <div>
                  <h3 className="text-lg font-['VT323'] text-black/80">{category.name}</h3>
                  <p className="text-sm text-black/60 font-['VT323']">{category.items.length} items</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`transform transition-transform ${expandedCategories[categoryKey] ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </div>
            </div>

            {expandedCategories[categoryKey] && (
              <div className="p-4 space-y-4">
                {/* Category Settings */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label 
                      className="block text-sm font-['VT323'] text-black/60 mb-2 admin-tooltip cursor-help"
                      data-tooltip="Visual icon representing this category"
                    >
                      Emoji
                    </label>
                    <input
                      type="text"
                      value={category.emoji}
                      onChange={(e) => updateCategory(categoryKey, 'emoji', e.target.value)}
                      className="w-full px-3 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200 text-center"
                      maxLength="2"
                      placeholder="🎨"
                    />
                  </div>
                  <div>
                    <label 
                      className="block text-sm font-['VT323'] text-black/60 mb-2 admin-tooltip cursor-help"
                      data-tooltip="Display name for this category of style options"
                    >
                      Category Name
                    </label>
                    <input
                      type="text"
                      value={category.name}
                      onChange={(e) => updateCategory(categoryKey, 'name', e.target.value)}
                      className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
                      placeholder="Category Name"
                    />
                  </div>
                  <div>
                    <label 
                      className="block text-sm font-['VT323'] text-black/60 mb-2 admin-tooltip cursor-help"
                      data-tooltip="Maximum number of items users can select from this category"
                    >
                      Max Selections
                    </label>
                    <select
                      value={category.maxSelections || 'unlimited'}
                      onChange={(e) => updateCategory(categoryKey, 'maxSelections', e.target.value === 'unlimited' ? null : parseInt(e.target.value))}
                      className="w-full px-4 py-3 pr-12 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
                      style={{
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 16px center',
                        backgroundSize: '14px'
                      }}
                    >
                      <option value="1">1 only</option>
                      <option value="2">Up to 2</option>
                      <option value="3">Up to 3</option>
                      <option value="5">Up to 5</option>
                      <option value="unlimited">Unlimited</option>
                    </select>
                  </div>
                  <div>
                    <label 
                      className="block text-sm font-['VT323'] text-black/60 mb-2 admin-tooltip cursor-help"
                      data-tooltip="How selected items from this category are combined in the AI prompt"
                    >
                      Combine Type
                    </label>
                    <select
                      value={category.combineType}
                      onChange={(e) => updateCategory(categoryKey, 'combineType', e.target.value)}
                      className="w-full px-4 py-3 pr-12 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
                      style={{
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 16px center',
                        backgroundSize: '14px'
                      }}
                    >
                      <option value="designers">Designers</option>
                      <option value="adjectives">Adjectives</option>
                      <option value="separate">Separate</option>
                      <option value="photographers">Photographers</option>
                    </select>
                  </div>
                </div>

                {/* Items Grid with Individual Item Editing */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {category.items.map((item, index) => (
                      <React.Fragment key={item.id}>
                        {/* Regular spice card */}
                        <div 
                          className={`bg-white rounded-xl border border-black/10 overflow-hidden transition-all duration-200 ${
                            editingItem && editingItem !== item.id 
                              ? 'opacity-40 hover:opacity-60' 
                              : editingItem === item.id 
                                ? 'shadow-lg' 
                                : 'hover:shadow-md'
                          }`}
                          style={editingItem === item.id ? {
                            transform: `rotate(${(item.id.charCodeAt(item.id.length - 1) % 5) - 2}deg)`
                          } : {}}
                        >
                          <div className="relative">
                            <div 
                              className={`p-4 transition-colors duration-200 ${
                                editingItem === item.id 
                                  ? 'cursor-default' 
                                  : 'cursor-pointer hover:bg-black/[0.02]'
                              }`}
                              onClick={() => editingItem !== item.id && setEditingItem(item.id)}
                            >
                              <div className="text-center mb-3">
                                <span className="text-3xl">{item.emoji}</span>
                              </div>
                              <h4 className="font-['VT323'] text-[16px] text-black/80 font-medium text-center mb-2">
                                {item.name}
                              </h4>
                              <p className="text-sm font-['VT323'] text-black/50 text-center mb-3">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Editing panel appears directly after the selected item */}
                        {editingItem === item.id && (
                          <div className="col-span-full bg-white rounded-xl border border-black/10 p-6 shadow-lg">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-['VT323'] text-black/60 mb-2">Emoji</label>
                                <input
                                  type="text"
                                  value={item.emoji}
                                  onChange={(e) => updateItem(categoryKey, item.id, 'emoji', e.target.value)}
                                  className="w-full px-3 py-2 bg-white/90 text-black border border-black/10 rounded-lg font-['VT323'] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-['VT323'] text-black/60 mb-2">Name</label>
                                <input
                                  type="text"
                                  value={item.name}
                                  onChange={(e) => updateItem(categoryKey, item.id, 'name', e.target.value)}
                                  className="w-full px-3 py-2 bg-white/90 text-black border border-black/10 rounded-lg font-['VT323'] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-['VT323'] text-black/60 mb-2">Prompt</label>
                                <textarea
                                  value={item.prompt}
                                  onChange={(e) => updateItem(categoryKey, item.id, 'prompt', e.target.value)}
                                  className="w-full px-3 py-2 bg-white/90 text-black border border-black/10 rounded-lg font-['VT323'] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 resize-none"
                                  rows="2"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-['VT323'] text-black/60 mb-2">Description</label>
                                <textarea
                                  value={item.description}
                                  onChange={(e) => updateItem(categoryKey, item.id, 'description', e.target.value)}
                                  className="w-full px-3 py-2 bg-white/90 text-black border border-black/10 rounded-lg font-['VT323'] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 resize-none"
                                  rows="2"
                                />
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingItem(null)}
                                  className="btn btn-green btn-sm"
                                >
                                  <FaSave />
                                  Save
                                </button>
                                <button
                                  onClick={() => setEditingItem(null)}
                                  className="btn btn-secondary btn-sm font-['VT323']"
                                >
                                  Cancel
                                </button>
                              </div>
                              <button
                                onClick={() => handleDeleteClick(categoryKey, item.id)}
                                className="btn btn-sm bg-red-500 hover:bg-red-600 text-white font-['VT323']"
                              >
                                <FaTrash />
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                    
                    {/* Add New Item Card - only show if not editing */}
                    {!editingItem && (
                      <div
                        onClick={() => addItem(categoryKey)}
                        className="bg-black/[0.02] hover:bg-black/[0.05] border-2 border-dashed border-black/20 rounded-xl p-6 cursor-pointer transition-colors duration-200 flex flex-col items-center justify-center min-h-[140px]"
                      >
                        <FaPlus className="w-6 h-6 text-black/40 mb-2" />
                        <p className="font-['VT323'] text-[14px] text-black/40 text-center">
                          Add New Spice
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Delete Confirmation Dialog */}
      {deletingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-['VT323'] text-black/80 mb-4">
              Are you sure you want to delete this spice?
            </h3>
                          <p className="text-sm text-black/60 mb-6 font-['VT323']">
                This action cannot be undone.
              </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="btn btn-secondary btn-sm font-['VT323']"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn btn-sm bg-red-500 hover:bg-red-600 text-white font-['VT323']"
              >
                <FaTrash />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpiceRackAdmin; 